import { useState, useContext, useMemo, type PropsWithChildren } from 'react'
import { Grid, Select, MenuItem, Hidden, useTheme, Pagination, PaginationItem } from '@hermes/web-components'
import MobileConditionsProceduresModal from '../../../search/MobileConditionsProceduresModal'
import { AppDataContext } from '../../../../providers/AppData'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import { SearchTypes, SortType } from '../../../../providers/SearchProfilesPage/types'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { Insurer, Language } from '../../../../types'
import AlternativeSearchBar from '../../Alternative/SearchBar'
import DefaultSearchFilter from '../../Default/SearchBar/SearchFilters'
import AlternativeSearchFilter from '../../Alternative/SearchBar/SearchFilters'
import ContentTopContainer from '../../../ContentTopContainer'
import { TEST_IDS, SORT_BY_OPTIONS } from '../../../../__tests__/testUtils/constants'
import useLayoutConfig from '../../../search/hooks/useLayoutConfig'
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags'
import { TENANTS } from '../../../../constants/global'
import { NUMBER_PER_PROFILE_REQUEST } from '../../../../constants/search'
import NextLink from 'next/link'
import useGetProfilesToShow from '../../../../hooks/useGetProfilesToShow'
import {
  ContentContainer,
  MediumBoldTypography,
  SearchLayoutContainer,
  SearchResultsLayout,
  SearchResultsTitle,
  TitleContainer
} from './styles'

type SearchLayoutProps = {
  languages: Language[]
  insurers: Insurer[]
} & PropsWithChildren<object>

const searchTotalTranslateKey = 'search.new_total_found'

// TODO: fix complexity
// eslint-disable-next-line complexity
function SearchLayout({ children, languages, insurers }: SearchLayoutProps) {
  const translate = useTranslateMessage()
  const theme = useTheme()

  const { alternativeSearchEnabled } = useFeatureFlags()

  const { language, locale } = useContext(AppDataContext)
  const {
    changeFilter,
    searchKeywordTitle,
    state: { filters, typeOfSpecialist, specialty, totalProfiles, currentLocation },
    buildUrl,
    changePage
  } = useContext(SearchProfilesPageContext)
  const profilesToShow = useGetProfilesToShow()
  const { displayComponents } = useLayoutConfig()

  const [isOpenConditionsProceduresModal, setIsOpenConditionsProceduresModal] = useState(false)

  const sorting = useMemo(() => {
    const defaultSorting = [
      {
        value: SortType.relevance,
        label: translate('relevance'),
        testId: `${TEST_IDS.SORT_BY}-${SORT_BY_OPTIONS.RELEVANCE}`
      },
      {
        value: SortType.nearest,
        label: translate('nearest'),
        testId: `${TEST_IDS.SORT_BY}-${SORT_BY_OPTIONS.NEAREST}`
      },
      {
        value: SortType.rating,
        label: translate('rating'),
        testId: `${TEST_IDS.SORT_BY}-${SORT_BY_OPTIONS.RATING}`
      }
    ]
    if (filters.searchType === SearchTypes.specialists) {
      defaultSorting.push({
        value: SortType.recommended,
        label: translate('most_endorsements'),
        testId: `${TEST_IDS.SORT_BY}-${SORT_BY_OPTIONS.MOST_ENDORSEMENTS}`
      })
    }

    return defaultSorting
  }, [filters.searchType, translate])

  const renderSearchFilters = (postAction?: () => void) =>
    alternativeSearchEnabled && locale !== TENANTS.WPA ? (
      <AlternativeSearchFilter languages={languages} insurers={insurers} specialties={typeOfSpecialist} />
    ) : (
      <DefaultSearchFilter
        postAction={postAction}
        languages={languages}
        insurers={insurers}
        specialties={typeOfSpecialist}
        specialtyName={specialty?.name[language] || ''}
      />
    )

  const location = filters.location || locale
  const parentLocation = filters.parentLocation ? `, ${filters.parentLocation}` : ''
  const decodedLocation = decodeURI(location + parentLocation)

  const translatedName = useMemo(() => {
    switch (filters.searchType) {
      case SearchTypes.healthAndSocialCare:
        return translate('home-care-specs')
      case SearchTypes.hospital:
        return translate('hospitals')
      case SearchTypes.pharmacy:
        return translate('pharmacies')
      case SearchTypes.specialists:
        return translate('specialists')
      case SearchTypes.careHome:
        return translate('care-home')
      default:
        return translate('practices')
    }
  }, [filters.searchType, translate])

  const getTranslatedTotalFound = () => {
    const locationText = decodeURIComponent(decodedLocation)

    switch (filters.searchType) {
      case SearchTypes.specialists:
        return translate(searchTotalTranslateKey, {
          location: locationText,
          name: specialty.id === 1 ? translate('search.all_specialists') : `${searchKeywordTitle || ''}`
        })

      case SearchTypes.dentist:
      case SearchTypes.practices:
        return translate('search.new_total_found-practice', {
          location: locationText,
          name: specialty?.name?.[language]
        })

      case SearchTypes.pharmacy:
      case SearchTypes.hospital:
      case SearchTypes.careHome:
      case SearchTypes.healthAndSocialCare:
        return translate(searchTotalTranslateKey, { location: locationText, name: translatedName })

      default:
        return translate(searchTotalTranslateKey, { location: locationText, name: specialty?.name?.[language] })
    }
  }

  const onChangePage = (_ev: unknown, newPage: number) => changePage(newPage)

  return (
    <SearchLayoutContainer container>
      <AlternativeSearchBar renderSearchFilters={renderSearchFilters} sorting={sorting} />
      <ContentTopContainer withKeywords={displayComponents.relatedKeywords} isSearchPage maxWidth="xl">
        <ContentContainer container>
          <Grid component={Hidden} lgDown implementation="css" xs={2}>
            <Hidden lgDown>{renderSearchFilters()}</Hidden>
          </Grid>
          <Grid container direction="column" overflow="hidden" gap="20px" justifyContent="space-between">
            <SearchResultsLayout alignContent="flex-start" container>
              <SearchResultsTitle container data-testid={TEST_IDS.SEARCH_RESULTS_TITLE} xs={12}>
                <TitleContainer>
                  <MediumBoldTypography component="h1">{getTranslatedTotalFound()} </MediumBoldTypography>
                  <MediumBoldTypography whiteSpace="nowrap">
                    {translate('search.results', { total: totalProfiles })}
                  </MediumBoldTypography>
                </TitleContainer>
                <Hidden lgDown>
                  <Select
                    size="small"
                    value={filters.sortType}
                    displayEmpty
                    onChange={(ev) => changeFilter('sortType', ev.target.value as string)}
                  >
                    {sorting.map((sort) => (
                      <MenuItem key={sort.value} value={sort.value}>
                        {sort.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Hidden>
              </SearchResultsTitle>
              {children}
            </SearchResultsLayout>
            {!!profilesToShow?.length && (
              <Pagination
                sx={{ ...(theme?.custom?.common?.pagination ?? {}), mb: 1, margin: '0 auto' }}
                page={filters.page}
                data-testid={TEST_IDS.SEARCH_RESULTS_PAGINATION}
                count={Math.ceil(totalProfiles / NUMBER_PER_PROFILE_REQUEST)}
                onChange={onChangePage}
                renderItem={(props: any) => {
                  const url = buildUrl({
                    page:
                      props.page > 1 && totalProfiles / NUMBER_PER_PROFILE_REQUEST <= props.page - 1
                        ? props.page - 1
                        : props.page,
                    profileType: filters?.profileType,
                    sortType: filters?.sortType,
                    typeOfSpecialist: filters?.typeOfSpecialist,
                    distance: filters?.distance,
                    gender: filters?.gender,
                    feeAgreed: filters?.feeAgreed,
                    patientsChildren: filters?.patientsChildren,
                    premium: filters?.premium,
                    slug: currentLocation?.slug,
                    language: filters?.language,
                    appointmentType: filters?.appointmentType,
                    insurer: filters?.insurer
                  })

                  return (
                    <PaginationItem
                      sx={theme?.custom?.common?.paginationItem ?? {}}
                      components={NextLink}
                      href={url}
                      shallow={true}
                      {...props}
                      data-testid={`${TEST_IDS.SEARCH_RESULTS_PAGINATION_BUTTON}${props.page}`}
                    />
                  )
                }}
              />
            )}
          </Grid>
        </ContentContainer>
      </ContentTopContainer>
      <MobileConditionsProceduresModal
        isOpen={isOpenConditionsProceduresModal}
        onClose={() => setIsOpenConditionsProceduresModal(false)}
        specialtyName={specialty?.name[language] || ''}
      />
    </SearchLayoutContainer>
  )
}

export default SearchLayout
